type StreamChunk = {
  content: string
  is_last_chunk: boolean
  is_last_message?: boolean
  error?: boolean
};

type StreamParams = {
  token: string
  userMessage: string | null
  onChunk: (chunk: StreamChunk) => void
  onError?: (err: any) => void
};

const streamAIResponse = async ({
  token,
  userMessage,
  onChunk,
  onError,
}: StreamParams) => {
  try {
    const response = await fetch('https://pinot.novoed.com/api/v1/role-play/learner/stream', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_message: userMessage,
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error('Failed to connect to AI stream');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    let buffer = '';

    const readNextChunk = async (): Promise<void> => {
      const { value, done } = await reader.read();
      if (done) return;

      buffer += decoder.decode(value, { stream: true });

      // SSE messages are separated by double newlines
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      let shouldCancel = false;

      parts.forEach((part) => {
        if (!part.startsWith('data:')) return;

        const json = part.replace(/^data:\s*/, '');

        try {
          const chunk: StreamChunk = JSON.parse(json);
          onChunk(chunk);

          if (chunk.is_last_chunk) {
            shouldCancel = true;
          }

          if (chunk.error) {
            shouldCancel = true;
            onError?.(new Error(`Error from AI stream: ${chunk.content}`));
          }
        } catch (err) {
          onError?.(err);
        }
      });

      if (shouldCancel) {
        await reader.cancel();
        return;
      }

      await readNextChunk();
    };

    await readNextChunk();
  } catch (err) {
    onError?.(err);
  }
};

export default streamAIResponse;
